<script>
import { computed, defineComponent, onMounted, ref, toRefs, watchEffect } from "vue";

import { AnswerStateEnum, QuizStateEnum } from "@/common/composables/use-vocab-quiz";
import { mapLanguageSpecificTexts } from "@/common/utils";
import QuizAnswerSummary from "@/components/vocabularyTrainer/QuizAnswerSummary.vue";
import QuizSelectorButton from "@/components/vocabularyTrainer/QuizSelectorButton.vue";
import { i18n } from "@/i18n";
import { useSettingsStore } from "@/stores";

import { initializeAnswerButtons, useGameComponent, watchState } from "./common";

const { commonProps, commonEmits } = useGameComponent();

export default defineComponent({
  components: { QuizAnswerSummary, QuizSelectorButton },
  props: commonProps,
  emits: commonEmits,
  /** @param {import("./common").Props} props */
  setup(props, { emit }) {
    const { t } = i18n.global;
    const { promptId, questions, quizState, answerState, explicitAnswerState, totalAnswerCount } =
      toRefs(props);

    const settingsStore = useSettingsStore();

    const answerButtons = ref([]);

    const question = computed(() => questions.value[0]);
    const isDisabled = computed(() => QuizStateEnum.Answered === quizState.value);

    const onAnswer = async (answer) => {
      const pressedButton = answerButtons.value.find((b) => b.value === answer);
      pressedButton.state = await props.answerValidator(question.value.id, answer);
      emit("answer", answer);
    };

    const languageSpecificTexts = computed(() =>
      mapLanguageSpecificTexts(
        {
          true: {
            english: t("vocab.games.english.true"),
            german: t("vocab.games.german.true")
          },
          false: {
            english: t("vocab.games.english.false"),
            german: t("vocab.games.german.false")
          }
        },
        settingsStore.activeLanguageGroup
      )
    );

    const resetAnswerButtons = () => {
      answerButtons.value = initializeAnswerButtons(question.value).map((btn) => {
        const text =
          btn.value === "true"
            ? languageSpecificTexts.value["true"]
            : languageSpecificTexts.value["false"];
        return {
          ...btn,
          text
        };
      });
    };

    onMounted(() => resetAnswerButtons());

    watchEffect(() =>
      watchState(question.value, answerState.value, answerButtons.value, explicitAnswerState.value)
    );

    return {
      AnswerStateEnum,

      explicitAnswerState,
      totalAnswerCount,

      answerButtons,
      promptId,
      onAnswer,
      isDisabled
    };
  }
});
</script>

<template>
  <div class="options">
    <div class="col-12 col-md-8 offset-md-2 options__container">
      <div v-for="(button, index) in answerButtons" :key="index">
        <QuizAnswerSummary
          v-if="explicitAnswerState && button.summary !== undefined"
          :key="explicitAnswerState"
          :total="totalAnswerCount"
          :count="button.summary"
          :color="button.state === AnswerStateEnum.Correct ? 'primary' : 'red'"
        />
        <QuizSelectorButton
          :state="button.state"
          :disabled="isDisabled"
          :aria-labelledby="`answer-text-${index} ${promptId}`"
          :text-id="`answer-text-${index}`"
          :text="button.text"
          lang="en"
          @click="onAnswer(button.value)"
        />
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";
.options__container {
  display: flex;
  flex-direction: column;
  row-gap: 15px;

  #media-breakpoint-up(lg, {
    row-gap: 20px;
  });

  #media-breakpoint-up(xxl, {
    row-gap: 30px;
  });
}
</style>
